/**
 * V2 Settings Service - Direct Integration
 * Replaces Firebase Functions with direct Firestore access where possible
 */

import { createMutation, createQuery } from "@tanstack/solid-query";
import { queryKeys } from "@core/query";
import { integrationsService } from "@core/services-v2";
import type { Integrations } from "@core/services-v2";
import type {
  DreameCloudConfig,
  DreameCloudConfigInput,
  DreameConnectionTestResult,
} from "./types";

const defaultConfig: DreameCloudConfig = {
  region: "us",
  countryCode: "US",
  languageCode: "en",
  username: null,
  deviceAlias: null,
  hasPassword: false,
  updatedAt: null,
  lastTestedAt: null,
  lastTestStatus: "unknown",
  lastTestMessage: null,
};

const mapIntegrationsToDreameConfig = (integrations: Integrations): DreameCloudConfig => {
  const dreameConfig = integrations.dreame;
  if (!dreameConfig) {
    return defaultConfig;
  }
  return {
    region: dreameConfig.region,
    countryCode: dreameConfig.countryCode,
    languageCode: dreameConfig.languageCode,
    username: dreameConfig.username,
    deviceAlias: dreameConfig.deviceAlias,
    hasPassword: dreameConfig.hasPassword,
    updatedAt: dreameConfig.updatedAt,
    lastTestedAt: dreameConfig.lastTestedAt,
    lastTestStatus: dreameConfig.lastTestStatus,
    lastTestMessage: dreameConfig.lastTestMessage,
  };
};

export const fetchDreameCloudConfig = async (): Promise<DreameCloudConfig> => {
  try {
    const integrations = await integrationsService.getDreameIntegration();
    return integrations ? mapIntegrationsToDreameConfig(integrations) : defaultConfig;
  } catch (error) {
    console.error('Error fetching Dreame config:', error);
    return defaultConfig;
  }
};

export const saveDreameCloudConfig = async (payload: DreameCloudConfigInput): Promise<DreameCloudConfig> => {
  try {
    // Update the integrations with new Dreame configuration
    const integrations = await integrationsService.saveDreameIntegration({
      region: payload.region,
      countryCode: payload.countryCode,
      languageCode: payload.languageCode,
      username: payload.username,
      deviceAlias: payload.deviceAlias,
      password: payload.password,
      hasPassword: Boolean(payload.password),
      updatedAt: new Date(),
    });

    // Return the updated config
    return mapIntegrationsToDreameConfig(integrations);
  } catch (error) {
    console.error('Error saving Dreame config:', error);
    throw error;
  }
};

export const testDreameConnection = async (): Promise<DreameConnectionTestResult> => {
  try {
    // NOTE: Connection testing still requires Firebase Functions for external API calls
    // This would need to be implemented as a function call or direct API integration
    console.warn('Dreame connection testing still requires Firebase Functions implementation');

    return {
      testedAt: new Date().toISOString(),
      message: "Connection testing not yet implemented in V2",
      devices: []
    };
  } catch (error) {
    console.error('Error testing Dreame connection:', error);
    return {
      testedAt: new Date().toISOString(),
      message: "Connection test failed",
      devices: []
    };
  }
};

export const useDreameConfigQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.settings.dreameConfig(),
    queryFn: () => fetchDreameCloudConfig(),
  }));

export const useSaveDreameConfigMutation = () =>
  createMutation(() => ({
    mutationFn: (payload: DreameCloudConfigInput) => saveDreameCloudConfig(payload),
  }));

export const useTestDreameConnectionMutation = () =>
  createMutation(() => ({
    mutationFn: () => testDreameConnection(),
  }));